
import React, { useState } from 'react';
import { Menu, Bell, ChevronLeft } from 'lucide-react';
import { useWindowScroll } from '@mantine/hooks';
import { MenuSheet } from './MenuSheet';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Button } from '@/components/ui/button';

interface HeaderProps {
  title?: string;
  subtitle?: string;
}

export const Header: React.FC<HeaderProps> = ({ title, subtitle }) => {
  const [scroll] = useWindowScroll();
  const scrolled = scroll.y > 10;
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isDashboard = location.pathname === '/dashboard';
  const { user } = useAuth();

  return (
    <header
      className={`sticky top-0 z-40 transition-shadow duration-300 px-4 md:px-6 py-3 text-foreground ${
        scrolled ? 'bg-background/80 backdrop-blur-md shadow-sm' : 'bg-background'}`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between md:ml-[var(--sidebar-width)] transition-[margin-left] duration-300">
        <div className="flex items-center gap-3">
          {!isDashboard && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full p-2"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
          )}

          {title && (
            <div className="animate-fade-in">
              <h1 className="font-heading text-lg md:text-xl uppercase tracking-[0.08em] text-foreground">{title}</h1>
              {subtitle && (
                <p className="text-xs md:text-sm text-muted-foreground">{subtitle}</p>
              )}
            </div>
          )}
        </div>

        {user && ( // Only show header content if user is logged in
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full p-2 relative"
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4 md:h-5 md:w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-led-red animate-pulse shadow-[0_0_5px_rgba(248,113,113,0.5)]"></span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="rounded-full p-2"
              aria-label="Menu"
              onClick={() => setMenuOpen(true)}
            >
              <Menu className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
            <MenuSheet open={menuOpen} onOpenChange={setMenuOpen} />
          </div>
        )}
      </div>
    </header>
  );
};
